import { useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { FileUp, FileText, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/core/AppLayout";
import { api, errMsg, fmtBytes } from "@/lib/api";

const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;
const blank = () => ({ numero: "", data_emissione: format(new Date(), "yyyy-MM-dd"), importo: "", scadenza: "", stato: "da_pagare" });

export const InvoiceQuickUpload = ({ clientId, clientName, onCreated }) => {
  const [f, setF] = useState(blank());
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const pick = (fl) => {
    if (!fl) return;
    if (!fl.name.toLowerCase().endsWith(".pdf")) return toast.error("Carica un file PDF");
    if (fl.size > 20 * 1024 * 1024) return toast.error("Il PDF supera i 20 MB");
    setFile(fl);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Il PDF della fattura è obbligatorio");
    setBusy(true);
    const fd = new FormData();
    fd.append("dati", JSON.stringify({ client_id: clientId, numero: f.numero, data_emissione: f.data_emissione, importo: Number(String(f.importo).replace(",", ".")),
      scadenza: f.scadenza, stato: f.stato }));
    fd.append("file", file);
    try {
      await api.post("/fatture/con-pdf", fd);
      toast.success(`Fattura ${f.numero} caricata per ${clientName}`);
      setF(blank()); setFile(null); onCreated();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <Panel className="p-5 mb-6 border-2 border-[#0A1128]/15" data-testid="inv-quick-upload">
      <div className="font-display text-xl font-black text-[#0A1128] mb-3">Carica fattura</div>
      <form onSubmit={submit} className="grid grid-cols-[1fr_1.4fr] gap-5">
        <div onClick={() => ref.current.click()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
          className={`cursor-pointer rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-center p-5 min-h-[170px] transition-colors ${drag ? "border-[#E8173A] bg-red-50/40" : file ? "border-[#0A1128]/40 bg-white/60" : "border-[#0A1128]/25 bg-[#A6DAFC] hover:border-[#E8173A]"}`}
          data-testid="inv-quick-dropzone">
          <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="inv-quick-pdf-input" />
          {file ? (
            <div className="text-sm text-[#0A1128]"><FileText className="h-7 w-7 mx-auto mb-2 text-[#E8173A]" /><div className="font-bold break-all" data-testid="inv-quick-file-name">{file.name}</div>
              <div className="text-xs text-slate-600">{fmtBytes(file.size)}</div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#E8173A]" data-testid="inv-quick-file-remove"><X className="h-3 w-3" />Rimuovi</button></div>
          ) : (
            <div className="text-sm text-[#0A1128]"><FileUp className="h-7 w-7 mx-auto mb-2" /><div className="font-bold">Trascina qui il PDF</div><div className="text-xs text-slate-600">o clicca per sceglierlo · obbligatorio · max 20 MB</div></div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 content-start">
          <div><L>Numero *</L><Input required value={f.numero} onChange={set("numero")} placeholder="Es. 12/2026" className="rounded-xl bg-white" data-testid="inv-quick-numero" /></div>
          <div><L>Importo totale (€) *</L><Input required inputMode="decimal" value={f.importo} onChange={set("importo")} className="rounded-xl bg-white font-bold" data-testid="inv-quick-importo" /></div>
          <div><L>Data emissione *</L><Input required type="date" value={f.data_emissione} onChange={set("data_emissione")} className="rounded-xl bg-white" data-testid="inv-quick-data" /></div>
          <div><L>Scadenza *</L><Input required type="date" value={f.scadenza} onChange={set("scadenza")} className="rounded-xl bg-white" data-testid="inv-quick-scadenza" /></div>
          <div><L>Stato</L><Select value={f.stato} onValueChange={set("stato")}><SelectTrigger className="rounded-xl bg-white" data-testid="inv-quick-stato"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="da_pagare">Da pagare</SelectItem><SelectItem value="pagata">Pagata</SelectItem></SelectContent></Select></div>
          <div className="flex items-end"><Button type="submit" disabled={busy} className="w-full rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="inv-quick-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileUp className="h-4 w-4 mr-1.5" />}Carica fattura</Button></div>
        </div>
      </form>
    </Panel>
  );
};
